import { DISCOUNT_IDS } from '@constants/apiConstants';
import { CURRENCY_CODE } from '@constants/app';
import { DIGITAL_HOME_PHONE } from '@features/Broadband/constants';
import {
  BasketMobile,
  BroadbandProductDetails,
  BundleQuoteFragment,
  Charge,
  ChargeType,
  EnergyProductDetails,
  ProductType,
  Quote,
} from '@generated/gql/graphql';
import { formatCurrency } from '@utils/format';
import { discountedMobileService, formatSimName } from '@utils/mobile';
import {
  calculateDiscountedPrice,
  calculateTotalWithDiscounts,
  getQuoteMonthlyCharge,
  getQuoteMonthlyPrice,
  sumPrices,
} from '@utils/price';

import { CombinedProduct, PriceDetail } from './types';

/**
 * When multiple quotes of the same type exist in the basket,
 * these are to be combined in to one "service". For example,
 * ENERGY is comprised of GAS and/or ELECTRICITY, but are shown
 * in the basket as a single, combined item.
 *
 * @param {Object[BundleQuoteFragment]} - basket quotes
 */

const combineProducts = (quotes: BundleQuoteFragment[]): CombinedProduct => {
  const combinedProduct = quotes.reduce<CombinedProduct>((product, quote) => {
    const allPrepayment = quotes.every((quote) =>
      isPrepaymentProduct(quote.product?.name)
    );
    const somePrepayment = quotes.some((quote) =>
      isPrepaymentProduct(quote.product?.name)
    );
    const isMixed = somePrepayment && !allPrepayment;

    const activeDiscounts = quote.discounts.filter(
      (discount) => discount.qualified
    );

    let quoteTotalMonthlyCost = parseFloat(quote.totalMonthlyCost.value);

    if (activeDiscounts.length > 0) {
      const discountedPrice = activeDiscounts.reduce((acc, discount) => {
        return calculateDiscountedPrice(acc, discount);
      }, quote.totalMonthlyCost);

      quoteTotalMonthlyCost = parseFloat(discountedPrice.value);
    }

    const { name, subtype, quoteType, isPrepayment } =
      getQuoteProductDetails(quote);

    if (Object.keys(product).length === 0) {
      return {
        id: quote.id,
        type: quote.product.type,
        name,
        details: quote.product.details as EnergyProductDetails,
        combinedTypes: subtype ? [subtype] : [],
        allPrepayment,
        isMixed: false,
        totalMonthlyCost: {
          ...quote.totalMonthlyCost,
          value: quoteTotalMonthlyCost.toFixed(2),
        },
        subtypeDetails: { [subtype]: { isPrepayment, quoteType, name } },
      };
    }

    const existingTotalMonthlyCost = parseFloat(
      product.totalMonthlyCost.value || '0'
    );

    const combinedTotalMonthlyCost =
      existingTotalMonthlyCost + quoteTotalMonthlyCost;
    return {
      ...product,
      combinedTypes: [...product.combinedTypes, subtype],
      allPrepayment,
      isMixed,
      totalMonthlyCost: {
        ...product.totalMonthlyCost,
        value: combinedTotalMonthlyCost.toFixed(2), // Formatting to 2 decimal places
      },
      subtypeDetails: {
        ...product.subtypeDetails,
        [subtype]: { isPrepayment, quoteType, name },
      },
    };
  }, {} as CombinedProduct);

  let charges: string[] = [];
  if (combinedProduct.isMixed) {
    charges = combinedProduct.combinedTypes.map((subtype) => {
      const detail = combinedProduct.subtypeDetails[subtype];
      return detail.isPrepayment
        ? `${subtype.toLowerCase()} ${detail.name.toLowerCase()}`
        : `${subtype.toLowerCase()} ${detail.quoteType?.toLowerCase()} tariff`;
    });
  } else {
    const combinedTypes = combinedProduct.combinedTypes
      .sort((a, b) => a.localeCompare(b))
      .join(' and ')
      .toLowerCase();
    const productOrQuote = combinedProduct.allPrepayment
      ? combinedProduct.name.toLowerCase()
      : `${combinedProduct.details?.quoteType?.toLowerCase()} tariff`;
    charges = [combinedTypes, productOrQuote];
  }

  return {
    ...combinedProduct,
    charges,
  };
};

const formatChargeName = (name: string): string => {
  const lowerCaseName = name.toLowerCase();

  switch (lowerCaseName) {
    case 'standard broadband':
      return 'standard';
    case 'line activation charge':
      return 'line activation';
    default:
      return name;
  }
};
const isPrepaymentProduct = (name?: string) =>
  name?.toLowerCase() === 'prepayment';

const getQuoteProductDetails = (quote: BundleQuoteFragment) => {
  const { name, subtype, details } = quote.product;
  const isPrepayment = isPrepaymentProduct(name);

  let quoteType: string | undefined;

  if (
    details &&
    (details as EnergyProductDetails).__typename === 'EnergyProductDetails'
  ) {
    quoteType = (details as EnergyProductDetails).quoteType;
  }

  return { name, subtype, quoteType, isPrepayment };
};

const formattedEnergy = (quotes: BundleQuoteFragment[]) => {
  if (!quotes || !quotes.length) return null;

  const combinedProduct = combineProducts(quotes);

  return {
    id: combinedProduct.id,
    type: combinedProduct.type,
    charges: combinedProduct.charges,
    total: formatCurrency({
      value: combinedProduct.totalMonthlyCost.value ?? '',
    }),
  };
};
const getEquipmentDeliveryCharge = (charges: Charge[]): Charge | undefined =>
  charges.find(
    (charge) =>
      charge.name === 'Equipment delivery charge' &&
      charge.type === ChargeType.OneOff
  );

const formattedBroadband = (product: BundleQuoteFragment | undefined) => {
  if (!product) {
    return null;
  }

  const activeDiscounts = product.discounts.filter(
    (discount) =>
      discount.id !== DISCOUNT_IDS.TBYB_BROADBAND_POSTAGE_DISCOUNT &&
      discount.qualified
  );

  const tryBeforeYouBuy = activeDiscounts.find(
    (discount) =>
      discount.id === DISCOUNT_IDS.TBYB_BROADBAND_LINE_DISCOUNT &&
      discount.qualified
  );
  const postageDiscount = product.discounts.find(
    (discount) =>
      discount.id === DISCOUNT_IDS.TBYB_BROADBAND_POSTAGE_DISCOUNT &&
      discount.qualified
  );

  const id = product.id;
  const allProductCharges = product.allProductCharges;
  const type = product.product.type;

  const monthlyCharges = allProductCharges.reduce(
    (chargeNames, { type, name }) => {
      if (type !== ChargeType.MonthlyRecurring && name !== DIGITAL_HOME_PHONE) {
        return chargeNames;
      }
      const formattedChargeName = formatChargeName(name);
      return [...chargeNames, formattedChargeName];
    },
    [] as string[]
  );

  let total = formatCurrency({
    value: product.totalMonthlyCost.value ?? '',
  });

  let totalAfterPromo = formatCurrency({
    value: product.totalMonthlyCost.value ?? '',
  });

  let afterPromo: PriceDetail | undefined;

  const contractLength =
    (product.product.details as BroadbandProductDetails).contractLength ?? 0;

  if (activeDiscounts) {
    const discountedTotal = calculateTotalWithDiscounts(
      product.totalMonthlyCost,
      getQuoteMonthlyCharge(product as Quote) as Charge,
      activeDiscounts
    );

    total = formatCurrency({ value: discountedTotal.value });
  }

  if (tryBeforeYouBuy) {
    totalAfterPromo = formatCurrency({ value: product.totalMonthlyCost.value });
    afterPromo = {
      text: `for ${tryBeforeYouBuy.creditFrequency} months, then`,
      value: totalAfterPromo,
      subtext: `for ${contractLength - tryBeforeYouBuy.creditFrequency} months (+£4 annual increase)`,
    };
  }

  let formattedCharges: string[] = Array.from(monthlyCharges);

  // If both "UW Wi-Fi Hub Pro" and "Amazon eero v6" are present, replace them with "Whole Home Wi-Fi"
  if (
    formattedCharges.includes('UW Wi-Fi Hub Pro') &&
    formattedCharges.includes('Amazon eero v6')
  ) {
    formattedCharges = formattedCharges.filter(
      (charge) => charge !== 'UW Wi-Fi Hub Pro' && charge !== 'Amazon eero v6'
    );
    formattedCharges.push('Whole Home Wi-Fi');
  }

  // The standard router is included free, so only mention it via "Whole Home Wi-Fi"
  formattedCharges = formattedCharges.filter(
    (charge) => charge !== 'UW Wi-Fi Hub Pro'
  );

  const requirement = product.requirements[0]?.requirement;

  if (
    requirement?.__typename === 'BroadbandQuoteRequirements' &&
    requirement.lineCharacteristics?.cli
  ) {
    formattedCharges.push(requirement.lineCharacteristics.cli);
  }

  const equipmentDeliveryCharge = getEquipmentDeliveryCharge(allProductCharges);

  let formattedOneTimeCost: PriceDetail | undefined;

  if (equipmentDeliveryCharge && !postageDiscount) {
    formattedOneTimeCost = {
      text: 'Plus',
      value: formatCurrency({
        value: equipmentDeliveryCharge.amount.value,
      }),
      subtext: 'P&P on first bill',
    };
  }

  return {
    total,
    type,
    id,
    charges: formattedCharges,
    oneTimeCost: formattedOneTimeCost,
    afterPromo,
  };
};

const getCbcActivationCharge = (charges: Charge[]): Charge | undefined =>
  charges.find(
    (charge) => charge.name === 'cbc-setup' && charge.type === ChargeType.OneOff
  );

const formattedCashbackCard = (product: BundleQuoteFragment) => {
  if (!product) {
    return null;
  }

  const quoteCast = product as Quote;
  const {
    allProductCharges,
    discounts,
    product: { type },
    totalMonthlyCost,
  } = quoteCast;
  const cbcActivationCharge = getCbcActivationCharge(allProductCharges);
  const activationDiscount = discounts.find(
    (discount) =>
      discount.id === DISCOUNT_IDS.CBC_ACTIVATION_CHARGE_DISCOUNT &&
      discount.qualified
  );
  const threeMonthsDiscount = discounts.find(
    (discount) =>
      discount.id === DISCOUNT_IDS.CBC_THREE_MONTHS_FREE_DISCOUNT &&
      discount.qualified
  );

  let formattedOneTimeCost: PriceDetail | undefined;

  if (cbcActivationCharge && !activationDiscount) {
    formattedOneTimeCost = {
      text: 'Plus',
      value: formatCurrency({
        value: cbcActivationCharge.amount.value,
      }),
      subtext: 'setup on first bill',
    };
  }

  let total = getQuoteMonthlyPrice(quoteCast);
  let afterPromo: PriceDetail | undefined;

  if (threeMonthsDiscount) {
    const discountedTotal = calculateDiscountedPrice(
      totalMonthlyCost,
      threeMonthsDiscount
    );

    total = formatCurrency({ value: discountedTotal.value });

    const totalAfterPromo = formatCurrency({ value: totalMonthlyCost.value });
    afterPromo = {
      text: `for ${threeMonthsDiscount.creditFrequency} months, then`,
      value: totalAfterPromo,
      subtext: `a month`,
    };
  }

  return {
    id: product.id,
    total,
    type,
    charges: [],
    oneTimeCost: formattedOneTimeCost,
    afterPromo,
  };
};

const formattedSimNames = (simNames: string[]) => {
  const namesSet = Array.from(new Set(simNames));

  const simSummaryObject = namesSet.reduce(
    (acc, name) => {
      acc[name] = 0;
      return acc;
    },
    {} as Record<string, number>
  );

  for (const simName of simNames) {
    simSummaryObject[simName]++;
  }

  const formattedSimNames = Object.keys(simSummaryObject).map((name) => {
    const quantity = simSummaryObject[name];
    const suffix = quantity > 1 ? ` x ${quantity}` : '';
    return `${name} SIM${suffix}`;
  });

  return formattedSimNames;
};

// Assumes only Multi Sim Unlimited Max Discount
const formattedMobile = (mobile: Nullable<BasketMobile>) => {
  if (!mobile || mobile.selectedSimCount === 0) {
    return null;
  }

  const { total, afterPromoTotal, durationMonths } =
    discountedMobileService(mobile);
  const simNames = mobile.sims.map((sim) =>
    formatSimName(sim.selectedTariff.name)
  );

  const afterPromo = durationMonths
    ? {
        text: `for ${durationMonths} months, then`,
        value: formatCurrency(afterPromoTotal),
        subtext: 'a month',
      }
    : undefined;

  return {
    id: ProductType.Mobile,
    total: formatCurrency(total),
    type: ProductType.Mobile,
    charges: formattedSimNames(simNames),
    afterPromo,
  };
};

const formattedInsurance = (products: BundleQuoteFragment[]) => {
  if (!products.length) {
    return null;
  }

  let boilerDiscountDuration = 0;

  const { total, subProducts } = products.reduce(
    (acc, quote) => {
      const {
        totalMonthlyCost,
        discounts,
        product: { name },
      } = quote;

      const threeMonthsDiscount = discounts.find(
        (discount) =>
          discount.id ===
            DISCOUNT_IDS.INSURANCE_BOILER_THREE_MONTHS_FREE_DISCOUNT &&
          discount.qualified
      );

      if (threeMonthsDiscount) {
        boilerDiscountDuration = threeMonthsDiscount.creditFrequency ?? 0;
      }

      let total = totalMonthlyCost;

      if (threeMonthsDiscount) {
        total = calculateDiscountedPrice(totalMonthlyCost, threeMonthsDiscount);
      }

      return {
        total: sumPrices(total, acc.total),
        subProducts: [...acc.subProducts, name],
      };
    },
    {
      total: { currency: CURRENCY_CODE, value: '0' },
      subProducts: [] as string[],
    }
  );

  let afterPromo: PriceDetail | undefined;

  if (boilerDiscountDuration > 0) {
    const totalAfterPromo = formatCurrency(
      products.reduce((acc, quote) => sumPrices(quote.totalMonthlyCost, acc), {
        currency: CURRENCY_CODE,
        value: '0',
      })
    );
    afterPromo = {
      text: `for ${boilerDiscountDuration} months, then`,
      value: totalAfterPromo,
      subtext: 'a month',
    };
  }

  return {
    id: ProductType.Insurance,
    total: formatCurrency(total),
    type: ProductType.Insurance,
    charges: subProducts,
    afterPromo,
  };
};

export {
  combineProducts,
  formattedEnergy,
  formattedBroadband,
  formattedCashbackCard,
  formattedMobile,
  formattedSimNames,
  formattedInsurance,
  getEquipmentDeliveryCharge,
};
